/**
 * HowToCard.jsx — Grid card for a How To entry.
 *
 * Video entries: thumbnail + play overlay. Desktop play swaps in the iframe
 * inline (one active player page-wide, managed by the parent via playing/onPlay).
 * On mobile the whole card opens the detail page — thumbnail-tap = play muscle
 * memory would fight precise targets on small screens.
 * Non-video entries: link card (domain) or article card (notes icon).
 */
import { useState } from 'react';
import { Box, Paper, Typography, Chip, IconButton } from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Link as LinkIcon,
  Notes as NotesIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { parseVideoUrl, urlDomain, isTrustedThumbnailHost } from '../../lib/videoUrl';

const KIND_LABELS = { howto: 'How-To', inspiration: 'Inspiration' };

export default function HowToCard({
  entry,
  playing = false,
  onPlay,
  onOpen,
  onEdit,
  canEdit = false,
  isMobile = false,
  showKind = false,
}) {
  const video = parseVideoUrl(entry.url);
  // Prefer a stored thumbnail only from a trusted provider CDN (blocks an
  // author-supplied off-domain image from beaconing every viewer); otherwise
  // fall back to the id-derived thumbnail (YouTube) or an icon.
  const storedThumb = isTrustedThumbnailHost(entry.thumbnailUrl) ? entry.thumbnailUrl : null;
  const thumb = storedThumb || video?.thumbnailUrl || null;

  // Track the src that failed to load; when `thumb` changes to a new value the
  // comparison auto-resets (no effect needed).
  const [brokenSrc, setBrokenSrc] = useState(null);
  const showThumb = thumb && brokenSrc !== thumb;

  const handleMediaClick = () => {
    if (video && !isMobile) onPlay?.(entry.id);
    else onOpen?.(entry.id);
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        overflow: 'hidden',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        transition: 'border-color 150ms, transform 150ms',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      {/* Media area — 16:9 */}
      <Box sx={{ position: 'relative', aspectRatio: '16 / 9', bgcolor: 'black', flexShrink: 0 }}>
        {playing && video ? (
          <iframe
            src={video.autoplayUrl}
            title={entry.title}
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
          />
        ) : (
          <Box
            onClick={handleMediaClick}
            sx={{
              position: 'absolute',
              inset: 0,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover .playBtn': { transform: 'scale(1.1)', bgcolor: 'rgba(239,68,68,0.95)' },
            }}
          >
            {showThumb ? (
              <Box
                component="img"
                src={thumb}
                alt={entry.title}
                loading="lazy"
                onError={() => setBrokenSrc(thumb)}
                sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <Box sx={{ textAlign: 'center', color: 'text.secondary', px: 2 }}>
                {video ? <PlayIcon sx={{ fontSize: 40, opacity: 0.6 }} />
                  : entry.url ? <LinkIcon sx={{ fontSize: 40, opacity: 0.6 }} />
                  : <NotesIcon sx={{ fontSize: 40, opacity: 0.6 }} />}
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>
                  {video ? (video.provider === 'instagram' ? 'Instagram' : video.provider === 'vimeo' ? 'Vimeo' : 'Video')
                    : entry.url ? urlDomain(entry.url) : 'Notes'}
                </Typography>
              </Box>
            )}
            {video && showThumb && (
              <Box
                className="playBtn"
                sx={{
                  position: 'relative',
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  bgcolor: 'rgba(0,0,0,0.65)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'transform 150ms, background-color 150ms',
                }}
              >
                <PlayIcon sx={{ color: 'white', fontSize: 32 }} />
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Text area — click opens detail */}
      <Box
        onClick={() => onOpen?.(entry.id)}
        sx={{ p: 1.5, cursor: 'pointer', display: 'flex', gap: 1, alignItems: 'flex-start', minHeight: 72 }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {entry.title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
            {showKind && (
              <Chip label={KIND_LABELS[entry.kind] || entry.kind} size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
            )}
            {entry.category && (
              <Chip label={entry.category} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
            )}
            {entry.bodyMd && (
              <NotesIcon sx={{ fontSize: 14, color: 'text.disabled' }} titleAccess="Has notes" />
            )}
          </Box>
        </Box>
        {canEdit && (
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onEdit?.(entry); }}
            sx={{ mt: -0.5, mr: -0.5, ...(isMobile && { p: 1.5, mt: -1, mr: -1 }) }}
          >
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>
    </Paper>
  );
}
